import React from 'react';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { FiClock, FiUsers, FiCpu, FiMessageSquare, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import Card from '../../components/ui/Card';

export default function ContributionRoom() {
  return (
    <div className="flex flex-col h-full bg-[#FAFAFA]">
      {/* Room Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 shrink-0 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Badge variant="insight">Live Deliberation</Badge>
            <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">Mission: Increase Q3 Pipeline</span>
          </div>
          <h1 className="text-xl font-semibold text-slate-900">What are we doing that creates activity but not qualified demand?</h1>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-600 bg-amber-50 px-3 py-1.5 rounded-md border border-amber-200">
            <SafeIcon icon={FiClock} /> 12:45 remaining
          </div>
          <div className="flex -space-x-2">
            {[1,2,3,4].map(i => (
              <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 shadow-sm">
                U{i}
              </div>
            ))}
            <div className="w-8 h-8 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-500 shadow-sm">
              +2
            </div>
          </div>
          <Button variant="primary">Move to Decision</Button>
        </div>
      </header>

      {/* Main Canvas Area */}
      <div className="flex-1 overflow-hidden flex">
        {/* Canvas */}
        <div className="flex-1 overflow-auto p-8 relative">
          
          {/* Deliberation Stage Indicator */}
          <div className="absolute top-8 left-1/2 -translate-x-1/2 bg-white px-4 py-2 rounded-full border border-slate-200 shadow-sm flex items-center gap-4 text-sm font-medium">
            <span className="text-slate-400">1. Divergence</span>
            <span className="w-4 h-[1px] bg-slate-300"></span>
            <span className="text-blue-600">2. Structure</span>
            <span className="w-4 h-[1px] bg-slate-300"></span>
            <span className="text-slate-400">3. Challenge</span>
            <span className="w-4 h-[1px] bg-slate-300"></span>
            <span className="text-slate-400">4. Convergence</span>
          </div>

          <div className="mt-20 grid grid-cols-3 gap-6">
            {/* Theme Column 1 */}
            <div className="bg-slate-50/50 rounded-xl p-4 border border-dashed border-slate-300">
              <h3 className="font-semibold text-slate-700 mb-4 flex items-center justify-between">
                Content Strategy Misalignment
                <span className="text-xs bg-white px-2 py-1 rounded border border-slate-200">3 items</span>
              </h3>
              <div className="space-y-3">
                <ContributionCard 
                  type="Observation"
                  content="Our recent whitepapers drive downloads but 80% use personal email addresses."
                  author="Marketing Ops"
                />
                <ContributionCard 
                  type="Idea"
                  content="Require business email for high-intent assets only."
                  author="Anonymous"
                />
              </div>
            </div>

            {/* Theme Column 2 */}
            <div className="bg-slate-50/50 rounded-xl p-4 border border-dashed border-slate-300">
              <h3 className="font-semibold text-slate-700 mb-4 flex items-center justify-between">
                SDR Qualification Criteria
                <span className="text-xs bg-white px-2 py-1 rounded border border-slate-200">2 items</span>
              </h3>
              <div className="space-y-3">
                 <ContributionCard 
                  type="Claim"
                  content="SDRs are incentivized on meetings booked, not pipeline generated."
                  author="Sales Leadership"
                  hasEvidence={true}
                />
              </div>
            </div>
            
            {/* Uncategorized */}
            <div className="bg-slate-50/50 rounded-xl p-4 border border-dashed border-slate-300 opacity-75">
              <h3 className="font-semibold text-slate-500 mb-4">Unsorted Contributions</h3>
              <ContributionCard 
                type="Question"
                content="Are we tracking the right leading indicators?"
                author="Data Team"
              />
            </div>
          </div>
        </div>

        {/* Right Panel - AI Facilitator */}
        <div className="w-80 bg-white border-l border-slate-200 flex flex-col">
          <div className="p-4 border-b border-slate-200 bg-purple-50/50">
            <h3 className="font-medium text-purple-900 flex items-center gap-2">
              <SafeIcon icon={FiCpu} /> AI Facilitator
            </h3>
          </div>
          <div className="flex-1 p-4 space-y-4 overflow-y-auto">
            <div className="bg-white border border-purple-200 rounded-lg p-3 shadow-sm">
              <p className="text-sm text-slate-700 mb-3">Two contributions appear similar but describe different causes for the SDR incentive issue.</p>
              <div className="flex gap-2">
                <Button variant="ai" size="sm" className="w-full">Suggest Merge</Button>
              </div>
            </div>
            
            <div className="bg-white border border-amber-200 rounded-lg p-3 shadow-sm">
              <div className="flex gap-2 items-start mb-2">
                <SafeIcon icon={FiAlertCircle} className="text-amber-500 mt-0.5" />
                <p className="text-sm text-slate-700">The leading idea depends on an untested assumption regarding buyer intent.</p>
              </div>
              <Button variant="secondary" size="sm" className="w-full text-amber-700 border-amber-200 hover:bg-amber-50">Flag Assumption</Button>
            </div>
          </div>
          <div className="p-4 border-t border-slate-200">
             <Button className="w-full justify-start gap-2" variant="secondary">
                <SafeIcon icon={FiMessageSquare} /> Add Contribution
             </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContributionCard({ type, content, author, hasEvidence }) {
  return (
    <Card className="p-3 cursor-grab active:cursor-grabbing hover:border-blue-300">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider bg-slate-100 px-1.5 py-0.5 rounded">
          {type}
        </span>
        {hasEvidence && <Badge variant="outcome"><SafeIcon icon={FiCheckCircle} className="mr-1"/>Evidence</Badge>}
      </div>
      <p className="text-sm text-slate-800 font-medium mb-3 leading-snug">{content}</p>
      <div className="text-xs text-slate-400 flex items-center justify-between">
        <span>{author}</span>
        <div className="flex gap-1">
           <button className="hover:text-blue-600"><SafeIcon icon={FiMessageSquare} /></button>
        </div>
      </div>
    </Card>
  );
}